import { useCallback, useEffect, useRef, useState } from "react";
import type { ClientePedidoStatusRow } from "../utils/mapPainelParaColunasCliente";
import { speakPedidoProntoRetirada } from "../utils/speakPedidoProntoRetirada";

const STORAGE_VOICE_KEY = "dom-belloni-tv-retirada-anunciar-voz";

const MODAL_SEGUNDOS = 14;

function snapshotPrepReady(
  emPreparo: ClientePedidoStatusRow[],
  prontos: ClientePedidoStatusRow[]
) {
  return {
    prep: new Set(emPreparo.map((x) => x.displayId)),
    ready: new Set(prontos.map((x) => x.displayId)),
  };
}

export function useRetiradaPedidoProntoModal(
  emPreparo: ClientePedidoStatusRow[],
  prontos: ClientePedidoStatusRow[]
) {
  const [modalPedido, setModalPedido] = useState<ClientePedidoStatusRow | null>(
    null
  );
  const [aguardandoDepoisEste, setAguardandoDepoisEste] = useState(0);
  const [segundosRestantes, setSegundosRestantes] = useState(MODAL_SEGUNDOS);
  /* Igual filosofia da TV Entregas: voz ligada por padrão; usuário só desliga com o botão. */
  const [voiceEnabled, setVoiceEnabled] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.localStorage.getItem(STORAGE_VOICE_KEY) !== "0";
  });

  const queueRef = useRef<ClientePedidoStatusRow[]>([]);
  const prevSnapRef = useRef<{ prep: Set<number>; ready: Set<number> } | null>(
    null
  );
  const seedDoneRef = useRef(false);
  const stopAnnouncementRef = useRef<(() => void) | null>(null);

  const toggleVoice = useCallback(() => {
    setVoiceEnabled((was) => {
      const next = !was;
      if (!next && stopAnnouncementRef.current) {
        stopAnnouncementRef.current();
        stopAnnouncementRef.current = null;
      }
      if (typeof window !== "undefined") {
        window.localStorage.setItem(STORAGE_VOICE_KEY, next ? "1" : "0");
        if (!next && "speechSynthesis" in window) {
          window.speechSynthesis.cancel();
        }
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const nextSnap = snapshotPrepReady(emPreparo, prontos);

    if (!seedDoneRef.current) {
      prevSnapRef.current = nextSnap;
      seedDoneRef.current = true;
      return;
    }

    const prev = prevSnapRef.current;
    prevSnapRef.current = nextSnap;
    if (!prev) return;

    const transitioned = prontos.filter(
      (p) => !prev.ready.has(p.displayId) && prev.prep.has(p.displayId)
    );

    const filaIds = new Set(queueRef.current.map((x) => x.displayId));
    let added = false;
    for (const row of transitioned) {
      if (filaIds.has(row.displayId)) continue;
      filaIds.add(row.displayId);
      queueRef.current.push(row);
      added = true;
    }

    /* Vários Prep→Pronto no mesmo tick: todos entram na fila; só o 1º abre modal, demais ficam na fila. */
    if (added) {
      setModalPedido((ativo) => ativo ?? queueRef.current.shift() ?? null);
      queueMicrotask(() => {
        setAguardandoDepoisEste(queueRef.current.length);
      });
    }
  }, [emPreparo, prontos]);

  useEffect(() => {
    if (!modalPedido) return undefined;

    setSegundosRestantes(MODAL_SEGUNDOS);
    let restante = MODAL_SEGUNDOS;

    const id = window.setInterval(() => {
      restante -= 1;
      setSegundosRestantes(restante);
      if (restante <= 0) {
        window.clearInterval(id);
        const seguinte = queueRef.current.shift() ?? null;
        setAguardandoDepoisEste(queueRef.current.length);
        setModalPedido(seguinte);
      }
    }, 1000);

    return () => window.clearInterval(id);
  }, [modalPedido]);

  useEffect(() => {
    if (!modalPedido || !voiceEnabled) {
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
      return undefined;
    }
    const stop = speakPedidoProntoRetirada(modalPedido.customerName);
    stopAnnouncementRef.current = stop;
    return () => {
      stop();
      stopAnnouncementRef.current = null;
    };
  }, [modalPedido, voiceEnabled]);

  useEffect(() => {
    return () => {
      stopAnnouncementRef.current?.();
      stopAnnouncementRef.current = null;
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  return {
    modalPedido,
    aguardandoDepoisEste: modalPedido != null ? aguardandoDepoisEste : 0,
    segundosRestantes: modalPedido != null ? segundosRestantes : MODAL_SEGUNDOS,
    voiceEnabled,
    toggleVoice,
  };
}
